// Ficha completa del activo: foto, mantenimientos y pólizas.
//
// Es lo que abre quien CUSTODIA el activo, no quien lo deprecia. La foto
// resuelve una toma física dudosa en dos segundos; el historial de
// mantenimientos dice cuándo deja de tener sentido reparar; y una cobertura
// vencida se descubre aquí y no el día que hay que cobrarla.
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Camera, Loader2, Plus, ShieldCheck, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { PanelIntegrado, useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, FichaAF, Mantenimiento, Opcion, Poliza, afRpc, fieldCls, listarProveedores, money } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

const TIPOS = [
  { v: 'correctivo', l: 'Correctivo (se dañó)' },
  { v: 'preventivo', l: 'Preventivo (programado)' },
  { v: 'calibracion', l: 'Calibración' },
];

export function FichaActivo({ activoId, placa, nombre, costo, onClose }: {
  activoId: string; placa: string; nombre: string; costo: number; onClose: () => void;
}) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [tab, setTab] = useState<'mant' | 'pol'>('mant');
  const [nuevoMant, setNuevoMant] = useState(false);
  const [nuevaPol, setNuevaPol] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const { data: ficha, isFetching } = useQuery<FichaAF>({
    queryKey: ['af-ficha', activoId], queryFn: () => afRpc<FichaAF>('af_ficha', { _activo_id: activoId }),
  });
  const { data: foto } = useQuery<{ foto: string | null }>({
    queryKey: ['af-foto', activoId], queryFn: () => afRpc<{ foto: string | null }>('af_foto', { _activo_id: activoId }),
  });
  const { data: proveedores = [] } = useQuery<Opcion[]>({ queryKey: ['af-proveedores'], queryFn: listarProveedores });
  const inval = () => {
    void qc.invalidateQueries({ queryKey: ['af-ficha', activoId] });
    void qc.invalidateQueries({ queryKey: ['af-foto', activoId] });
  };

  const setFoto = useMutation({
    mutationFn: (dataUri: string | null) => afRpc('af_foto_set', { _activo_id: activoId, _foto: dataUri }),
    onSuccess: () => { toast.success('Foto actualizada'); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Se reduce en el navegador antes de subir: una foto de 12 MP no aporta nada
  // para identificar un activo y el tope del servidor son 2 MB.
  const elegirFoto = (file: File) => {
    const img = new Image();
    const lector = new FileReader();
    lector.onload = () => {
      img.onload = () => {
        const max = 1024;
        const esc = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * esc); c.height = Math.round(img.height * esc);
        const ctx = c.getContext('2d');
        if (!ctx) { toast.error('No se pudo procesar la imagen'); return; }
        ctx.drawImage(img, 0, 0, c.width, c.height);
        setFoto.mutate(c.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => toast.error('El archivo no es una imagen válida');
      img.src = String(lector.result);
    };
    lector.readAsDataURL(file);
  };

  const guardarMant = useMutation({
    mutationFn: (v: Record<string, unknown>) => afRpc('af_mantenimiento_guardar', { _data: { ...v, activo_id: activoId } }),
    onSuccess: () => { toast.success('Mantenimiento registrado'); setNuevoMant(false); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const borrarMant = useMutation({
    mutationFn: (id: string) => afRpc('af_mantenimiento_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Eliminado'); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const guardarPol = useMutation({
    mutationFn: (v: Record<string, unknown>) => afRpc('af_poliza_guardar', { _data: { ...v, activo_id: activoId } }),
    onSuccess: () => { toast.success('Cobertura registrada'); setNuevaPol(false); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const borrarPol = useMutation({
    mutationFn: (id: string) => afRpc('af_poliza_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Eliminada'); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const res = ficha?.resumen;
  const pesado = (res?.costo_vs_activo_pct ?? 0) >= 50;

  return (
    <PanelIntegrado title={`${placa} · ${nombre}`} onClose={onClose} wide>
      {dlg.ui}
      <div className="grid gap-5 md:grid-cols-[220px_1fr]">
          {/* ---- foto ---- */}
          <div className="space-y-2">
            <div className="flex aspect-square items-center justify-center overflow-hidden rounded-md border bg-muted/40">
              {foto?.foto
                ? <img src={foto.foto} alt={placa} className="h-full w-full object-cover" />
                : <div className="p-4 text-center text-xs text-muted-foreground"><Camera className="mx-auto mb-2 h-6 w-6" />Sin foto</div>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) elegirFoto(f); e.target.value = ''; }} />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" isLoading={setFoto.isPending} onClick={() => fileRef.current?.click()}>
                <Camera className="mr-1 h-4 w-4" /> {foto?.foto ? 'Cambiar' : 'Tomar foto'}
              </Button>
              {foto?.foto && <Button size="sm" variant="ghost" onClick={() => setFoto.mutate(null)}><Trash2 className="h-4 w-4" /></Button>}
            </div>
            <p className="text-[11px] text-muted-foreground">Se reduce antes de subirla. La foto es lo que resuelve una toma física dudosa.</p>
          </div>

          {/* ---- resumen + pestañas ---- */}
          <div className="min-w-0 space-y-3">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="rounded-md border p-2">
                <div className="text-xs text-muted-foreground">Mantenimientos</div>
                <div className="font-semibold tabular-nums">{res?.n_mantenimientos ?? 0}</div>
              </div>
              <div className="rounded-md border p-2">
                <div className="text-xs text-muted-foreground">Gastado en mantener</div>
                <div className="font-mono font-semibold tabular-nums">{money(res?.costo_mantenimiento ?? 0)}</div>
              </div>
              <div className={`rounded-md border p-2 ${pesado ? 'border-amber-400 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/15' : ''}`}
                title="Cuánto se ha gastado en mantener el activo frente a lo que costó">
                <div className="text-xs text-muted-foreground">Sobre el costo</div>
                <div className="font-semibold tabular-nums">{res?.costo_vs_activo_pct != null ? `${res.costo_vs_activo_pct}%` : '—'}</div>
              </div>
            </div>
            {pesado && (
              <p className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/15 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Ya se gastó en mantenerlo más de la mitad de lo que costó ({money(costo)}). Vale la pena comparar reparar contra reemplazar — y la NIC 36 lo trata como indicio de deterioro.
              </p>
            )}

            <div className="flex items-center gap-2 border-b">
              {([['mant', 'Mantenimientos'], ['pol', 'Seguros y garantías']] as const).map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                  {l}
                </button>
              ))}
              <div className="ml-auto pb-1">
                {tab === 'mant'
                  ? <Button size="sm" variant="outline" onClick={() => setNuevoMant(true)}><Plus className="mr-1 h-4 w-4" /> Registrar</Button>
                  : <Button size="sm" variant="outline" onClick={() => setNuevaPol(true)}><Plus className="mr-1 h-4 w-4" /> Registrar</Button>}
              </div>
            </div>

            {isFetching && <div className="py-4 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" /></div>}

            {tab === 'mant' && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Tipo</th>
                    <th className="py-2 pr-3">Descripción</th><th className="py-2 pr-3">Proveedor</th>
                    <th className="py-2 pr-3 text-right">Costo</th><th className="py-2 pr-3">Próximo</th><th className="py-2"></th>
                  </tr></thead>
                  <tbody>
                    {(ficha?.mantenimientos ?? []).length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin mantenimientos registrados</td></tr>}
                    {(ficha?.mantenimientos ?? []).map((m: Mantenimiento) => (
                      <tr key={m.id} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 whitespace-nowrap">{m.fecha}</td>
                        <td className="py-1.5 pr-3"><Wrench className="mr-1 inline h-3 w-3 text-muted-foreground" />{m.tipo}</td>
                        <td className="py-1.5 pr-3">{m.descripcion}</td>
                        <td className="py-1.5 pr-3 text-muted-foreground">{m.proveedor ?? '—'}</td>
                        <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(m.costo)}</td>
                        <td className="py-1.5 pr-3 text-xs text-muted-foreground">{m.proxima_fecha ?? '—'}</td>
                        <td className="py-1.5 text-right">
                          <button className="text-red-500 hover:text-red-600" title="Eliminar"
                            onClick={async () => { if (await dlg.confirmar('¿Eliminar el mantenimiento?', { peligro: true })) borrarMant.mutate(m.id); }}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'pol' && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Clase</th><th className="py-2 pr-3">Número</th>
                    <th className="py-2 pr-3">Emisor</th><th className="py-2 pr-3">Vigencia</th>
                    <th className="py-2 pr-3 text-right">Suma asegurada</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
                  </tr></thead>
                  <tbody>
                    {(ficha?.polizas ?? []).length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin seguros ni garantías registrados</td></tr>}
                    {(ficha?.polizas ?? []).map((z: Poliza) => (
                      <tr key={z.id} className="border-b last:border-0">
                        <td className="py-1.5 pr-3">{z.clase}</td>
                        <td className="py-1.5 pr-3 font-mono text-xs">{z.numero ?? '—'}</td>
                        <td className="py-1.5 pr-3">{z.emisor ?? '—'}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap text-xs">{z.desde} → {z.hasta}</td>
                        <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{z.suma_asegurada != null ? money(z.suma_asegurada) : '—'}</td>
                        <td className="py-1.5 pr-3">
                          {z.vigente
                            ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"><ShieldCheck className="h-3 w-3" /> vigente{z.dias_para_vencer <= 45 ? ` · vence en ${z.dias_para_vencer} d` : ''}</span>
                            : <span className="text-xs font-semibold text-red-600 dark:text-red-400">vencida</span>}
                        </td>
                        <td className="py-1.5 text-right">
                          <button className="text-red-500 hover:text-red-600" title="Eliminar"
                            onClick={async () => { if (await dlg.confirmar('¿Eliminar la cobertura?', { peligro: true })) borrarPol.mutate(z.id); }}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-xs text-muted-foreground">
                  Los vencimientos avisan solos: entran a la bandeja de Auditoría Continua 45 días antes, sin que nadie tenga que abrir esta pantalla.
                </p>
              </div>
            )}
          </div>
      </div>

      {nuevoMant && <FormMant proveedores={proveedores} onCancel={() => setNuevoMant(false)}
        onSave={(v) => guardarMant.mutate(v)} guardando={guardarMant.isPending} />}
      {nuevaPol && <FormPoliza onCancel={() => setNuevaPol(false)}
        onSave={(v) => guardarPol.mutate(v)} guardando={guardarPol.isPending} />}
    </PanelIntegrado>
  );
}

function FormMant({ proveedores, onCancel, onSave, guardando }: {
  proveedores: Opcion[]; onCancel: () => void; onSave: (v: Record<string, unknown>) => void; guardando: boolean;
}) {
  const [v, setV] = useState({ fecha: fechaLocal(), tipo: 'correctivo', descripcion: '', proveedor_id: '', costo: 0, horas_parada: '', proxima_fecha: '' });
  return (
    <div className="border-t bg-muted/20 px-5 py-4">
      <h3 className="mb-3 text-sm font-semibold">Registrar mantenimiento</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <Campo label="Fecha"><input type="date" className={fieldCls} value={v.fecha} onChange={(e) => setV({ ...v, fecha: e.target.value })} /></Campo>
        <Campo label="Tipo"><select className={fieldCls} value={v.tipo} onChange={(e) => setV({ ...v, tipo: e.target.value })}>{TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}</select></Campo>
        <Campo label="Costo"><input type="number" step="0.01" min="0" className={fieldCls} value={v.costo} onChange={(e) => setV({ ...v, costo: Number(e.target.value) })} /></Campo>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <Campo label="Descripción *"><input className={fieldCls} value={v.descripcion} onChange={(e) => setV({ ...v, descripcion: e.target.value })} placeholder="Qué se hizo" /></Campo>
        </div>
        <Campo label="Proveedor">
          <select className={fieldCls} value={v.proveedor_id} onChange={(e) => setV({ ...v, proveedor_id: e.target.value })}>
            <option value="">— Ninguno —</option>
            {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social ?? p.nombre}</option>)}
          </select>
        </Campo>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Campo label="Horas de parada"><input type="number" step="0.1" min="0" className={fieldCls} value={v.horas_parada} onChange={(e) => setV({ ...v, horas_parada: e.target.value })} /></Campo>
        <Campo label="Próximo mantenimiento"><input type="date" className={fieldCls} value={v.proxima_fecha} onChange={(e) => setV({ ...v, proxima_fecha: e.target.value })} /></Campo>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Si el trabajo <b>capitaliza</b> —aumenta la vida útil o la capacidad—, regístralo además como Mejora: es ahí donde sube el costo del activo.</p>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button isLoading={guardando} disabled={!v.descripcion.trim()}
          onClick={() => onSave({ ...v, proveedor_id: v.proveedor_id || null, horas_parada: v.horas_parada || null, proxima_fecha: v.proxima_fecha || null })}>Guardar</Button>
      </div>
    </div>
  );
}

function FormPoliza({ onCancel, onSave, guardando }: {
  onCancel: () => void; onSave: (v: Record<string, unknown>) => void; guardando: boolean;
}) {
  const [v, setV] = useState({ clase: 'seguro', numero: '', emisor: '', desde: fechaLocal(), hasta: '', suma_asegurada: '', prima: '', deducible: '', cobertura: '' });
  return (
    <div className="border-t bg-muted/20 px-5 py-4">
      <h3 className="mb-3 text-sm font-semibold">Registrar seguro o garantía</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <Campo label="Clase"><select className={fieldCls} value={v.clase} onChange={(e) => setV({ ...v, clase: e.target.value })}><option value="seguro">Seguro</option><option value="garantia">Garantía</option></select></Campo>
        <Campo label="Número"><input className={fieldCls} value={v.numero} onChange={(e) => setV({ ...v, numero: e.target.value })} /></Campo>
        <Campo label="Emisor"><input className={fieldCls} value={v.emisor} onChange={(e) => setV({ ...v, emisor: e.target.value })} placeholder="Aseguradora o proveedor" /></Campo>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-4">
        <Campo label="Desde *"><input type="date" className={fieldCls} value={v.desde} onChange={(e) => setV({ ...v, desde: e.target.value })} /></Campo>
        <Campo label="Hasta *"><input type="date" className={fieldCls} value={v.hasta} onChange={(e) => setV({ ...v, hasta: e.target.value })} /></Campo>
        <Campo label="Suma asegurada"><input type="number" step="0.01" min="0" className={fieldCls} value={v.suma_asegurada} onChange={(e) => setV({ ...v, suma_asegurada: e.target.value })} /></Campo>
        <Campo label="Deducible"><input type="number" step="0.01" min="0" className={fieldCls} value={v.deducible} onChange={(e) => setV({ ...v, deducible: e.target.value })} /></Campo>
      </div>
      <div className="mt-3">
        <Campo label="Cobertura"><input className={fieldCls} value={v.cobertura} onChange={(e) => setV({ ...v, cobertura: e.target.value })} placeholder="Qué cubre" /></Campo>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button isLoading={guardando} disabled={!v.desde || !v.hasta}
          onClick={() => onSave({ ...v, suma_asegurada: v.suma_asegurada || null, prima: v.prima || null, deducible: v.deducible || null })}>Guardar</Button>
      </div>
    </div>
  );
}
